/**
 * Award governance — the cases raised to lapse, retain or cancel tranches, and
 * the pickers the "Raise a Governance Case" wizard walks through.
 */

import type {
  GovernanceActionType,
  GovernanceAward,
  GovernanceCaseAction,
  GovernanceCaseDetail,
  GovernanceCaseListItem,
  GovernanceEmployee,
  GovernanceEmployeeAward,
  GovernanceSummary,
  GovernanceTranche,
  GovernanceVestingSchedule,
} from '@/services/tenant/award-governance-services';

import { TOUR_AWARDS } from './tour-sample-awards';
import { TOUR_EMPLOYEES } from './tour-sample-people';

export const TOUR_GOVERNANCE_ACTION_TYPES: readonly GovernanceActionType[] =
  Object.freeze([
    { id: 1, actionName: 'Lapse' },
    { id: 2, actionName: 'Retain' },
    { id: 3, actionName: 'Cancel' },
    { id: 4, actionName: 'Accelerate Vesting' },
  ]);

export const TOUR_GOVERNANCE_EMPLOYEES: readonly GovernanceEmployee[] =
  Object.freeze(
    TOUR_EMPLOYEES.map(employee => ({
      userId: employee.id,
      employeeId: employee.employeeId,
      employeeName: employee.fullName,
      email: employee.email,
    }))
  );

export const TOUR_GOVERNANCE_AWARDS: readonly GovernanceAward[] = Object.freeze(
  TOUR_AWARDS.map(award => ({
    awardNo: award.awardNo,
    planName: award.planName,
    awardStatus: award.statusName,
    employees: 1,
    tranches: award.vestingSchedule.length,
  }))
);

export const TOUR_GOVERNANCE_VESTING_SCHEDULES: readonly GovernanceVestingSchedule[] =
  Object.freeze([
    {
      id: 801,
      name: 'Immediate',
      vestingTypeId: 1,
      vestingTypeName: 'Immediate',
      noOfYears: 0,
    },
    {
      id: 802,
      name: '1-Year Cliff',
      vestingTypeId: 2,
      vestingTypeName: 'Cliff',
      noOfYears: 1,
    },
    {
      id: 803,
      name: '2-Year Cliff',
      vestingTypeId: 2,
      vestingTypeName: 'Cliff',
      noOfYears: 2,
    },
  ]);

/** Tranches of the demo awards, as the case wizard's preview lists them. */
const trancheRows = (): GovernanceTranche[] =>
  TOUR_AWARDS.slice(0, 3).flatMap(award =>
    award.vestingSchedule.map(tranche => ({
      performanceShareId: award.id * 10 + tranche.sNo,
      userId:
        TOUR_EMPLOYEES.find(
          employee => employee.employeeId === award.employeeId
        )?.id ?? TOUR_EMPLOYEES[0].id,
      employeeId: award.employeeId,
      employeeName: award.employeeName,
      awardNo: award.awardNo,
      tranche: tranche.sNo,
      shares: tranche.sharesVested,
      shareStatus: 'Unvested',
      vestingDate: tranche.vestingDate,
      isEligible: tranche.sNo > 1,
      ineligibleReason: tranche.sNo === 1 ? 'Tranche has already vested' : null,
      actionShares: tranche.sharesVested,
      availableShares: tranche.sharesVested,
      isContingent: award.statusName === 'Action Required',
      awardType: award.planType,
      awardStatus: award.statusName,
      newVestingDate: null,
      newVestingScheduleId: null,
      newVestingScheduleName: null,
    }))
  );

export const TOUR_GOVERNANCE_TRANCHES: readonly GovernanceTranche[] =
  Object.freeze(trancheRows());

const summaryOf = (
  tranches: readonly GovernanceTranche[]
): GovernanceSummary => ({
  employees: new Set(tranches.map(tranche => tranche.employeeId)).size,
  awards: new Set(tranches.map(tranche => tranche.awardNo)).size,
  tranches: tranches.length,
  shares: tranches.reduce((total, tranche) => total + tranche.shares, 0),
});

export const TOUR_GOVERNANCE_SUMMARY: GovernanceSummary = summaryOf(
  TOUR_GOVERNANCE_TRANCHES
);

/** The preview grouped by employee + award, as the Add Action step shows it. */
export const TOUR_GOVERNANCE_EMPLOYEE_AWARDS: readonly GovernanceEmployeeAward[] =
  Object.freeze(
    TOUR_AWARDS.slice(0, 3).map(award => {
      const tranches = TOUR_GOVERNANCE_TRANCHES.filter(
        tranche => tranche.awardNo === award.awardNo
      );
      return {
        userId: tranches[0]?.userId ?? TOUR_EMPLOYEES[0].id,
        employeeId: award.employeeId,
        employeeName: award.employeeName,
        awardNo: award.awardNo,
        totalShares: tranches.reduce((total, t) => total + t.shares, 0),
        eligibleTranches: tranches.filter(t => t.isEligible).length,
        totalTranches: tranches.length,
        totalAvailableShares: tranches.reduce(
          (total, t) => total + t.shares,
          0
        ),
        isContingent: award.statusName === 'Action Required',
        awardType: award.planType,
        awardStatus: award.statusName,
        vestingDates: award.vestingSchedule.map(tranche => ({
          tranche: tranche.sNo,
          vestingDate: tranche.vestingDate,
        })),
        tranches,
      };
    })
  );

export const TOUR_GOVERNANCE_CASES: readonly GovernanceCaseListItem[] =
  Object.freeze([
    {
      id: 71,
      caseId: 71,
      txnId: 'AGOV-TOUR-00000071',
      caseName: 'Q3 2025 Leaver Lapses',
      effectiveDate: '2025-09-30',
      description: 'Lapse unvested tranches for employees who left in Q3.',
      createdBy: 'Mei Ling Chua',
      createdAt: '2025-09-12T10:22:00Z',
      lastMaker: 'Mei Ling Chua',
      lastMakerDatetime: '2025-09-12T10:22:00Z',
      approvedBy: 'Sarah Lim',
      approvedAt: '2025-09-14T09:05:00Z',
      status: 'Approved',
      workflowActionName: 'Create',
    },
    {
      id: 72,
      caseId: 72,
      txnId: 'AGOV-TOUR-00000072',
      caseName: 'Retention - Operations Restructure',
      effectiveDate: '2025-10-15',
      description: 'Retain tranches for transferred operations staff.',
      createdBy: 'Rajesh Menon',
      createdAt: '2025-09-20T14:40:00Z',
      lastMaker: 'Rajesh Menon',
      lastMakerDatetime: '2025-09-20T14:40:00Z',
      approvedBy: null,
      approvedAt: null,
      status: 'Pending Checker Approval',
      txnStatus: 'Pending Checker Approval',
      workflowActionName: 'Create',
    },
    {
      id: 73,
      caseId: 73,
      txnId: 'AGOV-TOUR-00000073',
      caseName: 'Accelerated Vesting - Retirement',
      effectiveDate: '2025-11-01',
      description: 'Accelerate vesting for an approved retirement.',
      createdBy: 'Priya Sharma',
      createdAt: '2025-09-22T08:15:00Z',
      lastMaker: 'Priya Sharma',
      lastMakerDatetime: '2025-09-22T08:15:00Z',
      approvedBy: null,
      approvedAt: null,
      status: 'Draft',
      workflowActionName: 'Create',
    },
  ]);

const caseAction = (actionTypeId: number): GovernanceCaseAction => ({
  id: 9100 + actionTypeId,
  actionTypeId,
  actionName:
    TOUR_GOVERNANCE_ACTION_TYPES.find(type => type.id === actionTypeId)
      ?.actionName ?? 'Lapse',
  isByEmployee: true,
  selectionType: 'By Employee',
  effectiveDate: '2025-09-30',
  noticeTemplateId: 4102,
  noticeTemplateName: 'Final Award Vesting Letter',
  coverLetterTemplateId: 4101,
  coverLetterTemplateName: 'PSP Contingent Award Letter',
  summary: TOUR_GOVERNANCE_SUMMARY,
  details: [...TOUR_GOVERNANCE_TRANCHES],
  hiddenTrancheCount: 0,
  appliedAt: null,
});

/** The full detail behind a sample governance case. */
export const getTourGovernanceCase = (
  caseKey: string | number
): GovernanceCaseDetail => {
  const listItem =
    TOUR_GOVERNANCE_CASES.find(
      item =>
        String(item.caseId) === String(caseKey) ||
        item.txnId === String(caseKey)
    ) ?? TOUR_GOVERNANCE_CASES[0];

  return {
    caseId: listItem.caseId,
    txnId: listItem.txnId,
    caseName: listItem.caseName,
    description: listItem.description,
    fileName: 'governance-supporting-note.pdf',
    blobPath: null,
    isDraft: listItem.status === 'Draft',
    status: listItem.status,
    txnStatus: listItem.txnStatus ?? listItem.status,
    makerRemarks: 'Raised following the quarterly leaver review.',
    checkerRemarks:
      listItem.status === 'Approved' ? 'Checked against the plan rules.' : null,
    createdBy: listItem.createdBy,
    createdAt: listItem.createdAt,
    lastMaker: listItem.lastMaker,
    lastMakerDatetime: listItem.lastMakerDatetime,
    approvedBy: listItem.approvedBy,
    approvedAt: listItem.approvedAt,
    actions: [caseAction(1)],
  };
};
